// Load polyfills (once, on the top of our web app)
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import './index.css';
import 'share-api-polyfill';

/**
 * Frontend code running in browser
 */
import React, { StrictMode } from 'react';
import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { loadableReady } from '@loadable/component';
import ConfigContext from '@utils/ConfigContext';
import { sha256 } from 'crypto-hash';
import { hydrateRoot } from 'react-dom/client';
import { MemoryRouter } from 'react-router-dom';
import { type PageData } from 'src/server/server.interfaces';
import App from '../app/App';
import { type Config } from '../server/config';
import { initialiseSentry } from './sentry.utils';

const config = (window.__CONFIG__ ?? {}) as Config;
const initialProps = (window.__INITIAL_PROPS__ ?? {}) as PageData;
delete window.__CONFIG__;
delete window.__INITIAL_PROPS__;

initialiseSentry(config.app.SENTRY_DSN, config.app.SENTRY_RELEASE_VERSION);

/** Components added here will _only_ be loaded in the web browser, never for server-side rendering */
const render = () => {
  const sparrowUrl = config.app.SPARROW_URL ?? '';

  const link = new HttpLink({
    fetch,
    uri: sparrowUrl,
  });

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([
      // If not functional tests use persisted queries
      ApolloLink.split(
        () => !config.app.isFunctionalTests,
        createPersistedQueryLink({
          sha256,
          useGETForHashedQueries: true,
        })
      ),
      link,
    ]),
  });

  const pageData = {
    ...initialProps,
    sparrowUrl,
  };

  void loadableReady(() => {
    hydrateRoot(
      document.getElementById('root')!,
      <ApolloProvider client={client}>
        <ConfigContext.Provider value={config}>
          <MemoryRouter initialEntries={[initialProps.path ?? '']}>
            <StrictMode>
              <App {...pageData} />
            </StrictMode>
          </MemoryRouter>
        </ConfigContext.Provider>
      </ApolloProvider>
    );
  });
};

render();
